import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Site } from 'src/app/model/site';
import { AppState } from 'src/app/store/app.reducer';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { SiteDialogComponent } from '../../../sites/components/site-dialog/site.dialog.component';
import * as SiteActions from 'src/app/store/site/site.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, filter, map } from 'rxjs';
import { UserFile } from 'src/app/model/user-file';
import { selectSiteImages } from 'src/app/store/user-file/user-file.selector';
import { UserFileService } from 'src/app/services/user-file.service';
import { selectCurrentSite, selectSiteDeleted, selectSitesOnlineDevices } from 'src/app/store/site/site.selector';
import { selectCurrentLang, selectCurrentTechnician, selectIsImpersMode, selectTechnicians, selectUser } from 'src/app/store/user/user.selector';
import { User } from 'src/app/model/user';
import * as HomeActions from 'src/app/store/home/home.actions';
import { FilesDialogComponent } from 'src/app/modules/shared/components/files-dialog/files.dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { Lang } from 'src/app/model/lang';

@Component({
	selector: 'app-site-card-mobile',
	templateUrl: './site-card-mobile.component.html',
	providers: [ConfirmationService, DialogService],
	styles:
		`
		#siteCardMobileImage {
			max-width: 82.5px;
			max-height: 54.5px;
		}

		img {
			-webkit-user-drag: none;
			user-drag: none;
		}
		`
})

export class SiteCardMobileComponent implements OnInit, OnDestroy {
	@Input() site: Site;
	@Input() fromDialogSelector: boolean = false;
	@Output() closeDialog: EventEmitter<any> = new EventEmitter();

	siteImages$: Observable<UserFile[] | undefined>;
	siteImagesSub: Subscription | undefined;
	technicians$: Observable<User[] | undefined>;
	techniciansSub: Subscription | undefined;
	currentTechnician$: Observable<User | undefined>;
	currentTechnicianSub: Subscription | undefined;
	impersMode$: Observable<boolean | undefined>;
	impersModeSub: Subscription | undefined;
	siteDeleted$: Observable<boolean | undefined>;
	siteDeletedSub: Subscription | undefined;
	sitesOnlineDevices$: Observable<Site[] | undefined>;
	sitesOnlineDevicesSub: Subscription | undefined;
	currentSite$: Observable<Site | undefined>;
	currentSiteSub: Subscription | undefined;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	siteImage = { src: 'assets/images/tablet-bg.jpg' };
	technician: User;
	currentTechnician: User;
	impersMode: boolean;
	sitesOnlineDevice: Site;
	currentSite: Site;
	currentUser: User;

	menuitems: MenuItem[] = [];

	ref: DynamicDialogRef | undefined;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;

	screenWidth: number;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private userFileService: UserFileService,
		private translate: TranslateService,
		public dialogService: DialogService,
		private router: Router,
		private route: ActivatedRoute,
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.subscribeAll();
		this.menuitems = [
			{ label: this.translate.instant('EDIT'), icon: 'pi cm-pencil', command: () => this.editSite(), visible: this.site.CurrentlyOwned === true && !this.isVctSharedSite(this.site) },
			{ label: this.translate.instant('FILES_LIST'), icon: 'pi cm-paperclip', command: () => this.filesDialog(), visible: this.site.CurrentlyOwned === true && !this.isVctSharedSite(this.site) },
			{ label: this.translate.instant('DELETE'), icon: 'pi cm-trash', command: () => this.deleteSite() },
		];
		this.onResize();
	}

	initSelectors(): void {
		this.siteImages$ = this.store.select(selectSiteImages);
		this.technicians$ = this.store.select(selectTechnicians);
		this.currentTechnician$ = this.store.select(selectCurrentTechnician);
		this.impersMode$ = this.store.select(selectIsImpersMode);
		this.siteDeleted$ = this.store.select(selectSiteDeleted);
		this.sitesOnlineDevices$ = this.store.select(selectSitesOnlineDevices);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.currentSite$ = this.store.select(selectCurrentSite);
		this.currentUser$ = this.store.select(selectUser);
	}

	checkLanguage(): boolean {
		return (this.currentLang.code !== "ru-RU");
	}

	subscribeAll(): void {
		this.siteImagesSub = this.siteImages$.subscribe((userFiles) => {
			const userFile = userFiles.find(f => f.SiteId === this.site.Id);
			if (!userFile) {
				return;
			}
			this.siteImagesSub = this.userFileService.getUserFileContent(userFile.Id)
				.pipe(filter(resp => !!resp))
				.subscribe(resp => {
					this.siteImage = { src: resp };
				});
		});
		this.techniciansSub = this.technicians$.pipe(map(technicians => technicians.find(tech => tech.Id === this.site.TechnicianId))).subscribe(technician => {
			this.technician = technician;
		});
		this.currentTechnicianSub = this.currentTechnician$.subscribe(currentTechnician => {
			this.currentTechnician = currentTechnician;
		});
		this.impersModeSub = this.impersMode$.subscribe(impersMode => {
			this.impersMode = impersMode;
		});
		this.siteDeletedSub = this.siteDeleted$.subscribe(deleted => {
			if (deleted) {
				this.store.dispatch(SiteActions.deleteSiteCleanup());
			}
		});
		this.sitesOnlineDevicesSub = this.sitesOnlineDevices$?.subscribe(
			(sitesOnlineDevices) => {
				this.sitesOnlineDevice = sitesOnlineDevices.find(s => s.Id === this.site.Id);
			}
		);
		this.currentLang$.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.currentSiteSub = this.currentSite$?.subscribe((currentSite) => {
			this.currentSite = currentSite;
		});
		this.currentUserSub = this.currentUser$?.subscribe(
			(currentUser) => {
				this.currentUser = currentUser;
			}
		);
	}

	ngOnDestroy(): void {
		this.siteImagesSub?.unsubscribe();
		this.sitesOnlineDevicesSub?.unsubscribe();
		this.currentSiteSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
	}

	imageSource(): { src: string } {
		return this.siteImage;
	}

	selectSite(): void {
		this.store.dispatch(SiteActions.setCurrentSite({ siteId: this.site.Id }));
		if (!this.fromDialogSelector) {
			this.router.navigate(['../devices'], { relativeTo: this.route });
		}
		if (this.fromDialogSelector) {
			this.closeDialog.emit(null);
		}
	}

	editSite(): void {
		this.store.dispatch(SiteActions.resetAddEditSiteForm());
		this.ref = this.dialogService.open(SiteDialogComponent, {
			header: this.translate.instant('EDIT_SITE'),
			width: '85vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				site: this.site
			},
		});
	}

	deleteSite(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.site.CurrentlyOwned ? this.translate.instant('DELETE_SITE') : this.translate.instant('DELETE_SHARED_SITE'),
			message: this.site.CurrentlyOwned ? this.translate.instant('DELETE_SITE_WARNING') : this.translate.instant('DELETE_SHARED_SITE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				if (this.site.CurrentlyOwned) {
					this.store.dispatch(SiteActions.deleteSite({ id: this.site.Id }));
				} else {
					this.store.dispatch(SiteActions.deleteSharedSite({ id: this.site.Id }));
				}
			},
			reject: () => {
			}
		});
	}

	filesDialog(): void {
		this.ref = this.dialogService.open(FilesDialogComponent, {
			header: this.translate.instant('FILES_LIST') + ' ' + this.translate.instant('USER'),
			width: '75vw',
			breakpoints: { '1120px': '95vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				site: this.site,
				owner: true
			},
		});
	}

	getDeviceOnline(): string {
		if (this.sitesOnlineDevice === undefined || this.sitesOnlineDevice === null) {
			return '0';
		}
		return this.sitesOnlineDevice.DeviceOnline.toString();
	}

	getDeviceOffline(): string {
		if (this.sitesOnlineDevice === undefined || this.sitesOnlineDevice === null) {
			return '0';
		}
		return this.sitesOnlineDevice.DeviceOffline.toString();
	}

	getTechInfo(): string {
		if (!this.technician) {
			return `${this.currentTechnician?.FirstName} ${this.currentTechnician?.LastName}`;
		}
		return `${this.technician.FirstName} ${this.technician.LastName}`;
	}

	goToSupport(): void {
		if (this.impersMode) {
			return;
		}
		this.store.dispatch(HomeActions.selectSupportTab({ index: 1 }));
		this.router.navigate(['../support'], { relativeTo: this.route });
		if (this.fromDialogSelector) {
			this.closeDialog.emit(null);
		}
	}

	cardsBgColor(site: Site): string {
		if (this.fromDialogSelector) {
			return (site.Id === this.currentSite.Id) ? "bg-blue-400 hover:bg-blue-400" : "surface-400 hover:surface-500";
		}
		return '';
	}

	isVctSharedSite(site: any): boolean {
		// !! Note: site have OwnerId only if is a VctSite shared
		if (site && this.currentUser) {
			return site.isOnlySiteVct && site.OwnerId !== this.currentUser.Id;
		} else { // default
			return true; // limited functs available
		}
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

}
